import React, { useState, useEffect } from 'react';
import { Sparkles, X, Clock } from 'lucide-react';

/**
 * Sticky animated trust / urgency banner (limited onboarding slots).
 * Dismissible per-session, mobile-friendly.
 */
const OfferBanner = ({ onClaim }) => {
  const [dismissed, setDismissed] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('offerBannerDismissed') === 'true';
    setDismissed(wasDismissed);
  }, []);

  // Rolling 7-day countdown for urgency (resets per session)
  useEffect(() => {
    const stored = sessionStorage.getItem('offerEndsAt');
    let endsAt;
    if (stored) {
      endsAt = new Date(stored);
    } else {
      endsAt = new Date();
      endsAt.setDate(endsAt.getDate() + 3);
      endsAt.setHours(23, 59, 59, 0);
      sessionStorage.setItem('offerEndsAt', endsAt.toISOString());
    }

    const tick = () => {
      const now = new Date();
      const diff = endsAt - now;
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setTimeLeft({ days, hours, minutes, seconds });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
    sessionStorage.setItem('offerBannerDismissed', 'true');
  };

  if (dismissed) return null;

  return (
    <div className="relative z-[60] w-full bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 text-white shadow-lg overflow-hidden">
      {/* Animated stripes */}
      <div className="absolute inset-0 opacity-20 bg-[linear-gradient(45deg,rgba(255,255,255,0.15)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.15)_50%,rgba(255,255,255,0.15)_75%,transparent_75%)] bg-[length:30px_30px] animate-[slide_2s_linear_infinite]" />

      <div className="container mx-auto px-2 py-1.5 sm:py-2.5 relative">
        <div className="flex items-center justify-center gap-1.5 sm:gap-3 text-center">
          {/* Sparkle icon (hidden on mobile) */}
          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300 animate-pulse hidden md:block flex-shrink-0" />

          {/* Trust / urgency text - no pricing */}
          <span className="text-[10px] sm:text-sm font-bold flex items-center gap-1.5">
            <span>🚀 Limited onboarding slots this week</span>
            <span className="hidden sm:inline text-yellow-300">— go live in 24-48hr</span>
          </span>

          {/* Countdown (hidden on mobile, compact on tablet+) */}
          <div className="hidden lg:flex items-center gap-1 text-[10px] sm:text-[11px] font-medium bg-blue-700/40 px-2 py-1 rounded-full flex-shrink-0">
            <Clock className="w-3 h-3" />
            <span className="hidden xl:inline">Closes in</span>
            <span className="font-bold tabular-nums">
              {String(timeLeft.days).padStart(2, '0')}d {String(timeLeft.hours).padStart(2, '0')}h {String(timeLeft.minutes).padStart(2, '0')}m
            </span>
          </div>

          {/* Claim CTA - Hidden on mobile */}
          {onClaim && (
            <button
              onClick={onClaim}
              className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-yellow-400 text-blue-900 hover:bg-yellow-300 rounded-full text-[10px] sm:text-xs font-extrabold shadow-md hover:scale-105 active:scale-95 transition-all flex-shrink-0"
            >
              Start Now →
            </button>
          )}
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/20 transition-colors"
          aria-label="Dismiss offer"
        >
          <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </button>
      </div>

      <style>{`
        @keyframes slide {
          0% { background-position: 0 0; }
          100% { background-position: 30px 0; }
        }
      `}</style>
    </div>
  );
};

export default OfferBanner;
